/* eslint-disable react/button-has-type */
import { useLazyQuery } from '@apollo/client'
import { GET_DI, GET_STUDENT_CONTEXT } from '@landscape/gql'
import {
  ChartComponent,
  DECILE,
  OverlaySpinner,
  applicantIdState,
  createChart,
  searchBarFocusState,
} from '@landscape/ui-lib'
import { selectUserDiCode, setModalOptions } from '@landscape/ui-redux'
import { InstitutionInfo } from 'components/etc/InstitutionInfo'
import { useWindowSize } from 'hooks/useWindowSize'
import get from 'lodash/get'
import isBoolean from 'lodash/isBoolean'
import isEmpty from 'lodash/isEmpty'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useRecoilState, useSetRecoilState } from 'recoil'
import './StudentDashboard.scss'
import { AccessibilityPanel } from './d3/dashboard/accessibilityPanel'
import { initialDiData, initialStudentData } from './initialChartData'

interface QueryVars {
  input: Object
}

interface StudentDashboardProps {
  history: {
    length: Number
    action: String
    location: {
      pathname: String
      search: String
      hash: String
      key: String
    }
  }
  location: {
    pathname: String
    search: String
    hash: String
    key: String
  }
  match: {
    path: String
    url: String
    isExact: Boolean
    params: {
      applicantId: String
    }
  }
}

const StudentDashboard = (props: StudentDashboardProps) => {
  const history = useHistory()
  const location = useLocation()
  const [applicantId, setApplicantId] = useRecoilState(applicantIdState)
  const setSearchBarFocus = useSetRecoilState(searchBarFocusState)
  const dispatch = useDispatch()
  const di_code = useSelector(selectUserDiCode)
  const node = useRef(null)
  const dimensions = useWindowSize()
  const [showNational, setShowNational] = useState(true)
  const [overrideChartUnitsPref, setOverrideChartUnitsPref] = useState(false)
  const [tempChartUnitsSelection, setTempChartUnitsSelection] = useState('')

  // TODO handle the external link
  const paramApplicantId = get(props, 'match.params.applicantId')
  if (
    !isEmpty(paramApplicantId && paramApplicantId.trim()) &&
    paramApplicantId.trim() !== applicantId
  ) {
    setApplicantId(paramApplicantId.trim())
  }

  const [getStudentData, studentData] = useLazyQuery(GET_STUDENT_CONTEXT, {
    fetchPolicy: 'cache-and-network',
  })

  let [getDiData, diData] = useLazyQuery(GET_DI, {
    fetchPolicy: 'cache-and-network',
  })

  const onChartUnitsChange = (units: string) => {
    setOverrideChartUnitsPref(true)
    setTempChartUnitsSelection(units)
  }

  const [showSpinner, setShowSpinner] = useState(true)

  const { readDIQuery } = diData.data || initialDiData
  const { readStudentContext } = studentData.data || initialStudentData
  let isBieSchool = get(readStudentContext, 'bie_school') || false
  if (!isBoolean(isBieSchool)) {
    isBieSchool = isBieSchool === '1'
  }

  let isPOBox = get(readStudentContext, 'po_box') || false
  if (!isBoolean(isPOBox)) {
    // eslint-disable-next-line
    isPOBox = isPOBox == '1'
  }

  const instPrefChartDisplayUnits = get(readStudentContext, 'chartDisplayUnits') || DECILE

  useLayoutEffect(() => {
    if (studentData.loading || diData.loading) {
      if (!showSpinner) setShowSpinner(true)
      //  make sure the html and useQuery finish before trying to display the charts
    } else if (studentData.error || diData.error) {
      // catch //any errors
      // TODO handle this error properly
      console.error(studentData.error)
      if (showSpinner) setShowSpinner(false)
    } else if (
      !studentData.data ||
      !studentData.data.readStudentContext ||
      !studentData.data.readStudentContext.applicant_id
    ) {
      console.log('Student ID does not exist')
      if (showSpinner) setShowSpinner(false)
      if (location.pathname === '/chart') setSearchBarFocus(true)
    } else if (studentData.data) {
      if (showSpinner) setShowSpinner(false)

      if (get(studentData, 'data.readStudentContext.applicant_id') !== '-1' && diData.data) {
        const { readStudentContext } = studentData.data
        const { readDIQuery } = diData.data

        createChart({
          overrideChartUnitsPref,
          tempChartUnitsSelection,
          instPrefChartDisplayUnits,
          showNational,
          readStudentContext,
          readDIQuery,
          isInteractive: true,
        })
      }
    }

    return function cleanup() {
      /* window.removeEventListener('transitionend', null) */
      // d3.selectAll('svg.chart').remove()
    }
  }, [
    studentData.data,
    studentData.error,
    studentData.loading,
    showNational,
    dimensions.width,
    dimensions.height,
    diData.data,
    diData.loading,
    diData.error,
    tempChartUnitsSelection,
    studentData,
    showSpinner,
    overrideChartUnitsPref,
    instPrefChartDisplayUnits,
    location.pathname,
    setSearchBarFocus,
  ])

  useEffect(() => {
    document.title = 'Landscape - Chart'
  })

  useEffect(() => {
    if (applicantId && applicantId.trim().length >= 1) {
      getStudentData({
        variables: { input: { id: applicantId } },
      })
      getDiData()

      if (!paramApplicantId) {
        history.replace(`/chart/${applicantId}`)
      }

      let topLink: HTMLElement = document.querySelector(
        "a[href='#cb-atlas-header-1']"
      ) as HTMLElement
      topLink && topLink.click()
    }

    // Reset the 2 toggles back to their default.
    //  - Set chart display back to inst pref
    setTempChartUnitsSelection('')
    setOverrideChartUnitsPref(false)
    //  - National is the default for National vs. State
    setShowNational(true)
  }, [applicantId, getStudentData, di_code, getDiData, paramApplicantId, history])

  const percentTakingSAT =
    studentData.data &&
    studentData.data.readStudentContext &&
    +studentData.data.readStudentContext.a_pct_taking_sat

  const setAccessTables = () => {
    const accessibilityModalOptions = {
      showModal: true,
      body: [
        <AccessibilityPanel
          key="accessibility mode"
          props={{ readDIQuery, readStudentContext }}
        ></AccessibilityPanel>,
      ],
      status: 'success',
      title: `Accessibility Mode`,
      confirm: false,
    }
    dispatch(setModalOptions(accessibilityModalOptions))
  }

  const handleAccessibilityEnter = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setAccessTables()
    }
  }

  const handleAccessibilityClick = () => {
    setAccessTables()
  }

  const chartCompProps = {
    overrideChartUnitsPref,
    tempChartUnitsSelection,
    instPrefChartDisplayUnits,
    onChartUnitsChange,
    isPOBox,
    isBieSchool,
    percentTakingSAT,
    setShowNational,
    showNational,
    readStudentContext,
    isInteractive: true,
  }

  return (
    <div id="main-content">
      <OverlaySpinner show={showSpinner} ariaLabel="charts-spinner" />

      <InstitutionInfo />

      {readStudentContext.applicant_id === '-1' ||
      (!applicantId && !studentData.loading && !diData.loading) ? (
        <div
          ref={node}
          className="container display-flex flex-column justify-content-center align-items-center"
        >
          <label
            id="chartInstructions"
            className="cb-margin-top-16 cb-margin-bottom-16 cb-font-weight-bold cb-band cb-band-panel"
          >
            Enter Applicant ID to view Landscape chart
          </label>
        </div>
      ) : (
        <div className="cb-panel">
          <div className="container">
            <div className="display-flex flex-xs-column flex-md-row justify-md-content-between cb-margin-top-16 cb-margin-bottom-16 cb-padding-right-16 cb-padding-left-16">
              <div
                aria-label="Student ID"
                className="col-xs-12 col-md-6 program-tag  display-flex justify-content-center"
              >
                <span id="chart-applicant-id-header" className="cb-purple1-bg">
                  Applicant ID:
                  <strong>{` ${applicantId}`}</strong>
                </span>
              </div>
              <div className="col-xs-12 col-md-6 display-flex justify-content-xs-center cb-margin-xs-top-16 cb-margin-sm-top-16 justify-content-md-end">
                <button
                  data-cbtrack
                  id="accessibility-button"
                  className="cb-btn cb-btn-yellow"
                  aria-label="Accessibility Mode (Tabular alternatives to Landscape Charts and Graphs)"
                  aria-haspopup="dialog"
                  onKeyPress={handleAccessibilityEnter}
                  onClick={handleAccessibilityClick}
                >
                  Accessibility Mode
                </button>
              </div>
            </div>

            <ChartComponent {...chartCompProps} />
          </div>
        </div>
      )}
    </div>
  )
}

export { StudentDashboard }
